import { devLog } from '@/lib/logging';
import type { LeadB2CPayload } from '@/services/api/MarketingApiTypes';
import { TrackingData } from '@/utils/tracking';
import type { WizardFormData } from '../types';

/**
 * Get the submission URL for lead tracking.
 * Uses the intake-v2 base path without the step portion.
 *
 * @returns The full URL to the intake-v2 flow
 */
const getSubmissionUrl = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    if (window.location.href.includes('/get-started')) {
      return `${window.location.origin}/get-started`;
    }
  } catch (error) {
    devLog(error);
  }

  return `${window.location.origin}/intake-v2`;
};

/**
 * Build lead payload from wizard form data.
 *
 * @param formData - The wizard form data containing contact details
 * @returns Lead payload formatted for the Marketing API
 */
export const buildLeadPayload = (
  formData: WizardFormData,
  trackingData: TrackingData
): LeadB2CPayload => {
  const contact = formData.contact_details;
  if (!contact) {
    throw new Error('Contact details required to create lead');
  }

  return {
    firstName: contact.first_name,
    lastName: contact.last_name,
    email: contact.email,
    phoneNumber: contact.phone || '',
    zipCode: formData.zip_code || contact.postal_code || '',
    type: 'Individual/Family Member',
    lead_channel: 'Website Form',
    opportunity_type: 'New Membership',
    company: `${contact.first_name}, ${contact.last_name} Family`,
    source_details: 'Web Lead',
    membership_type: '',
    email_opt_in: contact.marketing_consent,
    submissionUrl: getSubmissionUrl(),
    VisitReason: formData.reasons,
    MembershipStartTimeframe: formData.urgency,
    PrimaryCareStatus: formData.pcp_status,
    SignupParticipantType: formData.signup_type,
    ours_device_id: trackingData.ours_device_id,
    vwo_uuid: trackingData.vwo_uuid,
    utm_campaign: trackingData.utm_campaign,
    utm_content: trackingData.utm_content,
    utm_medium: trackingData.utm_medium,
    utm_term: trackingData.utm_term,
    utm_source: trackingData.utm_source,
    hdyhau: formData.contact_details?.hdyhau,
    hdyhau_other: formData.contact_details?.hdyhau_other,
    gclid: trackingData.gclid,
    msclkid: trackingData.msclkid,
    fbclid: trackingData.fbclid,
    campaignId: trackingData.campaignId,
    templateId: trackingData.templateId,
    sms_opt_in: contact.sms_opt_in,
  };
};

/**
 * Create lead via API route.
 *
 * @param formData - The wizard form data containing contact details
 * @returns The created lead ID
 */
export const createLead = async (
  formData: WizardFormData,
  trackingData: TrackingData
): Promise<string> => {
  const payload = buildLeadPayload(formData, trackingData);

  const response = await fetch('/api/create-lead', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      `Failed to create lead: ${error.error || response.statusText}`
    );
  }

  const result = await response.json();
  return result.leadId;
};
